import { AlignContent } from '~/templates/blocks/shared/AlignContent';
import { LinkButton } from '~/shared/components/Button';
import { Headline } from '~/templates/blocks/shared/Headline';
import { RawHtml } from '~/shared/components/RawHtml';
import { Spacer } from '~/shared/components/Spacer';
import { Text } from '~/shared/components/Text';
import { Video } from '~/shared/components/Video';
import { queries } from '~/theme';
import { useModule } from '../../hooks/useModule';
import { useModuleTheme } from '../../hooks/useModuleTheme';
import { ModuleContainer } from '../../shared/ModuleContainer';
import {
    StyledDesktopImage,
    StyledDesktopMedia,
    StyledM75,
    StyledMobileImage,
    StyledMobileMedia,
} from './styled';

import { M76ContentPromotionProps } from './types';

export type { M76ContentPromotionProps } from './types';

export const M76ContentPromotion = ({
    image,
    mobileImage,
    video,
    headline,
    subHeadline,
    description,
    callToAction,
    callToActionTypeFill,
    mediaHorizontalAlignment = 'left',
    horizontalAlignment = 'left',
    verticalAlignment = 'top',
    backgroundColor: initialBackgroundColor,
    backgroundColorHEX: initialBackgroundColorHex,
    ...rest
}: M76ContentPromotionProps) => {
    const { index } = useModule();
    const { textColor, backgroundColor, buttonColor, buttonVariant } = useModuleTheme({
        backgroundColor: initialBackgroundColorHex || initialBackgroundColor,
        buttonVariant: callToActionTypeFill ? 'fill' : 'stroke',
    });

    const mediaType = video ? 'video' : 'image';
    const mobileMedia = mobileImage || image;

    return (
        <ModuleContainer hasGutter {...rest}>
            <StyledM75 backgroundColor={backgroundColor} mediaAlignment={mediaHorizontalAlignment}>
                <StyledDesktopMedia>
                    {mediaType === 'image' && image ? (
                        <StyledDesktopImage
                            src={image.src}
                            alt={image.alt ?? ''}
                            width={image?.width}
                            height={image?.height}
                            focalPoint={image.focalPoint}
                            sizes={`${queries.xs} 100vw, ${queries.contentMax} 50vw, 720px`}
                            priority={index < 2}
                        />
                    ) : null}
                    {mediaType === 'video' && video ? (
                        <Video
                            controls={false}
                            src={video.src}
                            loop={video.loop}
                            playing={video.autoPlay}
                            posterSrc={image?.src}
                        />
                    ) : null}
                </StyledDesktopMedia>
                <StyledMobileMedia>
                    {mobileMedia ? (
                        <StyledMobileImage
                            src={mobileMedia.src}
                            alt=""
                            sizes="30vw"
                            priority={index < 2}
                            focalPoint={mobileMedia.focalPoint}
                            width={mobileMedia?.width}
                            height={mobileMedia?.height}
                        />
                    ) : null}
                </StyledMobileMedia>
                <AlignContent
                    alignment={horizontalAlignment}
                    position={verticalAlignment}
                    textColor={textColor}
                >
                    {subHeadline ? <Text variant="caption">{subHeadline}</Text> : null}
                    {subHeadline ? <Spacer size="2" /> : null}
                    {headline ? <Headline variant="display2">{headline}</Headline> : null}
                    {headline ? <Spacer size="4" /> : null}
                    {description ? (
                        <Text variant="paragraph" as="div">
                            <RawHtml html={description} />
                        </Text>
                    ) : null}
                    {callToAction ? <Spacer size="3" /> : null}
                    {callToAction && (
                        <LinkButton
                            href={callToAction.url}
                            target={callToAction.target}
                            variant={buttonVariant}
                            color={buttonColor}
                        >
                            {callToAction.text}
                        </LinkButton>
                    )}
                </AlignContent>
            </StyledM75>
        </ModuleContainer>
    );
};
